import toPairs from 'lodash/toPairs';
import debounce from "lodash/debounce";
import { types as sdkTypes } from './sdkLoader';
import { diffInTime } from './dates';
import { countryCodes } from '../config/configCodes';
import { sendMsgBlockedWarning } from './api';

const debouncedSendWarning = debounce(sendMsgBlockedWarning, 8000);

const getNameAndEmailFromUser = user => {
  const { email, profile } = user?.attributes || {};
  const { firstName, lastName } = profile || {};

  return {
    email,
    name: `${firstName} ${lastName}`,
    firstName,
    lastName,
  };
};

const { LatLng, Money } = sdkTypes;

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;

const validationBlocker = "Message blocked: personal information being shared prior to an introduction.";

const isNonEmptyString = val => {
  return typeof val === 'string' && val.trim().length > 0;
};

/**
 * Validator functions and helpers for Final Forms
 */

// Final Form expects and undefined value for a successful validation
const VALID = undefined;

export const required = message => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return message;
  }
  if (typeof value === 'string') {
    // string must be nonempty when trimmed
    return isNonEmptyString(value) ? VALID : message;
  }
  return VALID;
};

export const requiredStringNoTrim = message => value => {
  return typeof value === 'string' && value.length > 0 ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredBoolean = message => value => {
  return typeof value === 'boolean' ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredAndNonEmptyString = message => value => {
  return isNonEmptyString(value) ? VALID : message;
};

export const requiredAndDoesNotIncludeCountryCode = message => value => {
  if (typeof value === 'undefined' || value === null) {
    return message;
  };

  if (typeof value === 'string') {
    const hasCountryCode = countryCodes.some(country => value.startsWith(country.dial_code));
    const isNotEmpty = isNonEmptyString(value);
    return isNotEmpty && !hasCountryCode ? VALID : message;
  };

  return VALID;
};

export const requiredFieldArrayCheckbox = message => value => {
  if (!value) {
    return message;
  }

  const entries = toPairs(value);
  const hasSelectedValues = entries.filter(e => !!e[1]).length > 0;
  return hasSelectedValues ? VALID : message;
};

export const requiredSelectTreeOption = message => value => {
  if (typeof value === 'undefined' || value === null || Object.values(value)?.length === 0) {
    return message;
  }
};

export const minLength = (message, minimumLength) => value => {
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length >= minimumLength ? VALID : message;
};

export const maxLength = (message, maximumLength) => value => {
  if (!value) {
    return VALID;
  }
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length <= maximumLength ? VALID : message;
};

export const isEqual = (message, matchValue) => value => {
  if (!value || !matchValue) {
    return VALID;
  }
  const equal = value == matchValue;
  return equal ? VALID : message;
};

export const nonEmptyArray = message => value => {
  return value && Array.isArray(value) && value.length > 0 ? VALID : message;
};

export const minOptionsArray = (message, minOptions) => value => {
  return value && Array.isArray(value) && value.length >= minOptions ? VALID : message;
};

export const autocompleteSearchRequired = message => value => {
  return value && value.search ? VALID : message;
};

export const autocompletePlaceSelected = message => value => {
  const selectedPlaceIsValid =
    value &&
    value.selectedPlace &&
    value.selectedPlace.address &&
    value.selectedPlace.origin instanceof LatLng;
  return selectedPlaceIsValid ? VALID : message;
};

export const bookingDateRequired = inValidDateMessage => value => {
  const dateIsValid = value && value.date instanceof Date;
  return !dateIsValid ? inValidDateMessage : VALID;
};

export const bookingDatesRequired = (inValidStartDateMessage, inValidEndDateMessage) => value => {
  const startDateIsValid = value && value.startDate instanceof Date;
  const endDateIsValid = value && value.endDate instanceof Date;

  if (!startDateIsValid) {
    return inValidStartDateMessage;
  } else if (!endDateIsValid) {
    return inValidEndDateMessage;
  } else {
    return VALID;
  }
};

// Source: http://www.regular-expressions.info/email.html
// See the link above for an explanation of the tradeoffs.
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const emailFormatValid = message => value => {
  return value && EMAIL_RE.test(value) ? VALID : message;
};

export const moneySubUnitAmountAtLeast = (message, minValue) => value => {
  return value instanceof Money && value.amount >= minValue ? VALID : message;
};

const parseNum = str => {
  const num = Number.parseInt(str, 10);
  return Number.isNaN(num) ? null : num;
};

export const numberAtLeast = (message, minNumber) => value => {
  const valueNum = parseNum(value);
  return typeof valueNum === 'number' && valueNum >= minNumber ? VALID : message;
};

export const ageAtLeast = (message, minYears) => value => {
  const { year, month, day } = value;
  const dayNum = parseNum(day);
  const monthNum = parseNum(month);
  const yearNum = parseNum(year);

  // day, month, and year needs to be numbers
  if (dayNum !== null && monthNum !== null && yearNum !== null) {
    const now = new Date();
    const age = new Date(yearNum, monthNum - 1, dayNum);
    const ageInYears = diffInTime(now, age, 'years', true);

    return age && age instanceof Date && ageInYears >= minYears ? VALID : message;
  }
  return message;
};

export const validBusinessURL = message => value => {
  if (typeof value === 'undefined' || value === null) {
    return message;
  }

  const disallowedChars = /[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/;
  const protocolTokens = value.split(':');
  const includesProtocol = protocolTokens.length > 1;
  const usesHttpProtocol = includesProtocol && !!protocolTokens[0].match(/^(https?)/);

  const invalidCharacters = !!value.match(disallowedChars);
  const invalidProtocol = !(usesHttpProtocol || !includesProtocol);
  // Stripe checks against example.com
  const isExampleDotCom = !!value.match(/^(https?:\/\/example\.com|example\.com)/);
  const isLocalhost = !!value.match(/^(https?:\/\/localhost($|:|\/)|localhost($|:|\/))/);
  return invalidCharacters || invalidProtocol || isExampleDotCom || isLocalhost ? message : VALID;
};

export const validSsnLast4 = message => value => {
  return value.length === 4 ? VALID : message;
};

export const validHKID = message => value => {
  // Accept value 000000000 for testing Stripe
  if (value.length === 9 && value.match(/([0]{9})/)) {
    return VALID;
  }

  // HKID format example: AB364912(5)
  // ID can start with one or two letters and the check digit in the end can be in brackets or not
  if (value.length < 8) {
    return message;
  }

  // Handle possible brackets in value
  if (value.charAt(value.length - 3) === '(' && value.charAt(value.length - 1) === ')') {
    value = value.substring(0, value.length - 3) + value.charAt(value.length - 2);
  }
  value = value.toUpperCase();

  // Check that pattern is correct and split value to array
  const hkidPattern = /^([A-Z]{1,2})([0-9]{6})([A0-9])$/;
  const matchArray = value.match(hkidPattern);

  if (!matchArray) {
    return message;
  }

  const charPart = matchArray[1];
  const numPart = matchArray[2];
  const checkDigit = matchArray[3];

  // Calculate the checksum for character part.
  // Transfer letters to numbers so that A=10, B=11, C=12 etc.
  // If there is only one letter in the ID use 36 as the first value
  // Total calculation is weighted so that 1st digit is x9, 2nd digit x8, 3rd digit x7 etc.

  const strValidChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let checkSum = 0;

  if (charPart.length === 2) {
    checkSum += 9 * (10 + strValidChars.indexOf(charPart.charAt(0)));
    checkSum += 8 * (10 + strValidChars.indexOf(charPart.charAt(1)));
  } else {
    checkSum += 9 * 36;
    checkSum += 8 * (10 + strValidChars.indexOf(charPart));
  }

  // Calculate the checksum for numeric part

  for (let i = 0, j = 7; i < numPart.length; i++, j--) {
    checkSum += j * numPart.charAt(i);
  }

  // Verify the check digit
  const remaining = checkSum % 11;
  let verify = remaining === 0 ? 0 : 11 - remaining;
  verify = verify.toString();
  const isValid = verify === checkDigit || (verify === 10 && checkDigit === 'A');

  return isValid ? VALID : message;
};

export const validSGID = message => value => {
  return value.length === 9 ? VALID : message;
};

export const composeValidators = (...validators) => value =>
  validators.reduce((error, validator) => error || validator(value), VALID);

const emailPattern = /[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/;
const phonePattern = /(\+?\d{1,4}[\s-]?)?(\(?\d{3}\)?[\s-]?)?[\d\s-]{7,10}/;
const moreThan9Digits = /[0-9]\d{9,}/

const majorEmailProviders = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'icloud.com',
  'aol.com',
  'protonmail.com',
  'zoho.com',
  'mail.com',
];

const socialHandles = [
  'facebook.com',
  'twitter.com',
  'instagram.com',
  'linkedin.com',
  'tiktok.com',
  'snapchat.com',
  'pinterest.com',
  'slack.com',
  'reddit.com',
  'facebook',
  'twitter',
  'instagram',
  'linkedin',
  'tiktok',
  'snapchat',
  'pinterest',
  'reddit',
];

const numberWords = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
};

const containsEmail = description => emailPattern.test(description);

// Updated phone number check using the dial codes from countryCodes
const containsPhone = description => {
  const phoneMatch = description.match(phonePattern) || description.match(moreThan9Digits);
  if (phoneMatch) {
    const phoneNumber = phoneMatch[0];

    if (phoneNumber.includes("0000")) {
      return false;
    };

    // Check if the phone number contains any dial code from the countryCodes array
    const hasCountryCode = countryCodes.some(country => phoneNumber?.startsWith(country?.dial_code));
    return hasCountryCode;
  }
  return false;
};

const containsObfuscatedNumber = description => {
  const lowerDescription = description.toLowerCase();

  const extractedNumbers = Object.keys(numberWords).reduce((acc, word) => {
    const regex = new RegExp(word, 'g');
    const matches = lowerDescription.match(regex);
    if (matches) {
      acc.push(...matches.map(() => numberWords[word]));
    }
    return acc;
  }, []);

  return extractedNumbers.length >= 9; // Adjust the threshold as needed
};

const containsMajorEmailProvider = description => {
  return majorEmailProviders.some(provider => description.includes(provider));
};

const containsSocialHandle = description => {
  return socialHandles.some(handle => description.includes(handle));
};

const containsAtSymbol = description => description.includes('@');

// `transactionId` is only available once a transaction exists (the transaction
// chat). On the pre-transaction enquiry forms pass `listingId` instead, so the
// admin warning email can still link somewhere useful.
export const validateInputField = (currentUser, transactionId, listingId) => value => {
  if (!value) {
    return null;
  };

  const { name, email } = getNameAndEmailFromUser(currentUser);

  const hasEmail = containsEmail(value);
  const hasPhone = containsPhone(value);
  const hasObfuscatedNumber = containsObfuscatedNumber(value);
  const hasEmailProvider = containsMajorEmailProvider(value);
  const hasSocialHandle = containsSocialHandle(value);
  const hasAtSymbol = containsAtSymbol(value);

  const reason = hasEmail
    ? "contains email"
    : hasPhone
      ? "contains phone"
      : hasObfuscatedNumber
        ? "contains obfuscated number"
        : hasEmailProvider
          ? "contains email provider"
          : hasSocialHandle
            ? "contains social handle"
            : hasAtSymbol
              ? "contains @ symbol"
              : "Unknown";

  if (
    hasEmail ||
    hasPhone ||
    hasObfuscatedNumber ||
    hasEmailProvider ||
    hasSocialHandle ||
    hasAtSymbol
  ) {
    debouncedSendWarning({
      name,
      email,
      msg: value,
      reason,
      transactionId,
      listingId,
    });

    return validationBlocker;
  };

  return null;
};

const checkContainsSensitiveInfo = (msg, userData, transactionId, listingId) => {
  const { firstName, lastName, email, name } = userData;
  if (!(firstName || lastName || email)) return false;

  // Normalize the message and display name by trimming and converting to lower case
  const normalizedMsg = msg.toLowerCase();
  const normalizedFirstName = firstName.toLowerCase().trim();
  const normalizedLastName = lastName.toLowerCase().trim();
  const normalizedEmail = email.toLowerCase().trim();
  const domain = normalizedEmail.slice(normalizedEmail.indexOf("@") + 1)
  const potentialCompanyName = domain.slice(0,domain.indexOf("."))

  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const matchesWholeWord = (word) => word && new RegExp(`\\b${escapeRegex(word)}\\b`).test(normalizedMsg);
  const hasName = matchesWholeWord(normalizedFirstName) || matchesWholeWord(normalizedLastName);
  const reason = "includes name";

  if (
    hasName
    // || normalizedMsg.includes(potentialCompanyName);
  ) {
    debouncedSendWarning({
      name,
      email,
      msg,
      reason,
      transactionId,
      listingId,
    });

    return true;
  };

  return hasName;
};

// Returns the list of human-readable reasons (field types) caught in a message,
// matching the same detection used by validateInputField and
// validateNoSensitiveInfoInMessage. Used to surface a warning popup to the user.
export const getBlockedReasons = (message, currentUser) => {
  if (!message) {
    return [];
  };

  const { firstName, lastName } = getNameAndEmailFromUser(currentUser);

  const reasons = [];

  const hasEmail = containsEmail(message) || containsMajorEmailProvider(message);
  const hasPhone = containsPhone(message);
  const hasObfuscatedNumber = containsObfuscatedNumber(message);
  const hasSocialHandle = containsSocialHandle(message);
  // A full email already contains "@", so avoid the redundant "@ Symbol" reason.
  const hasAtSymbol = !hasEmail && containsAtSymbol(message);

  const normalizedMsg = message.toLowerCase();
  const escapeRegex = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const matchesWholeWord = word =>
    word && new RegExp(`\\b${escapeRegex(word.toLowerCase().trim())}\\b`).test(normalizedMsg);
  const hasName = matchesWholeWord(firstName) || matchesWholeWord(lastName);

  if (hasPhone) reasons.push('Phone Number');
  if (hasEmail) reasons.push('Email address');
  if (hasName) reasons.push('First or Last Name');
  if (hasObfuscatedNumber) reasons.push('Obfuscated phone');
  if (hasSocialHandle) reasons.push('Social Handle');
  if (hasAtSymbol) reasons.push('@ Symbol');

  return reasons;
};

// See `validateInputField` above for when to pass `transactionId` vs `listingId`.
export const validateNoSensitiveInfoInMessage = (message, currentUser, transactionId, listingId) => {
  if (!message) {
    return null; // No message to validate
  }

  const currentUserData = getNameAndEmailFromUser(currentUser);

  const containsSensitiveInfo = checkContainsSensitiveInfo(
    message,
    currentUserData,
    transactionId,
    listingId
  );

  if (containsSensitiveInfo) {
    return validationBlocker;
  };

  return null;
};